import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, fileSrc } from "../../lib/apiClient";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Plus, Trash2, Camera } from "lucide-react";
import { toast } from "sonner";

const lbl = "text-xs uppercase tracking-wider text-[#5C5651]";
const fieldCls = "mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]";
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }); } catch { return ""; } };

export default function PhotosTab({ projectId, readOnly }) {
  const { user } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/projects/${projectId}/photos`).then((r) => setPhotos(r.data)).catch(() => {});
  useEffect(() => { load(); }, [projectId]);

  const pick = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const reset = () => { setFile(null); setPreview(null); setTitle(""); setNotes(""); };

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      fd.append("notes", notes);
      const { data } = await api.post(`/projects/${projectId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((p) => [data, ...p]);
      reset(); setOpen(false);
      toast.success("Photo uploaded");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/photos/${id}`); setPhotos((p) => p.filter((x) => x.id !== id)); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const canDelete = (photo) => !readOnly && (user?.role === "designer" || photo.uploaded_by_id === user?.id);

  return (
    <div className="max-w-6xl space-y-6" data-testid="photos-tab">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A68A6B] mb-1">Gallery</p>
          <h3 className="font-serif text-3xl text-[#2B2622]">Photos</h3>
        </div>
        {!readOnly && (
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-photo-btn"
              className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5 px-5">
              <Plus size={15} className="mr-2" /> Add Photo
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none border-[#EBE5E0] bg-white">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-[#2B2622]">Add photo</DialogTitle>
              <DialogDescription className="sr-only">Upload a project photo with a title and notes</DialogDescription>
            </DialogHeader>
            <form onSubmit={upload} className="space-y-4 mt-2">
              <div>
                <Label className={lbl}>Photo</Label>
                <input data-testid="photo-file-input" type="file" accept="image/*" onChange={pick}
                  className="mt-1 block w-full text-sm text-[#5C5651] file:mr-3 file:border file:border-[#A68A6B] file:bg-transparent file:text-[#A68A6B] file:px-4 file:py-2 file:uppercase file:text-xs file:tracking-wider" />
                {preview && <img src={preview} alt="preview" data-testid="photo-preview" className="mt-3 w-full max-h-56 object-cover border border-[#EBE5E0]" />}
              </div>
              <div>
                <Label className={lbl}>Title</Label>
                <Input data-testid="photo-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Living room — after install" className={fieldCls} />
              </div>
              <div>
                <Label className={lbl}>Notes</Label>
                <Textarea data-testid="photo-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes about this photo" className={fieldCls} />
              </div>
              <Button data-testid="photo-upload-submit" type="submit" disabled={busy || !file}
                className="w-full rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5">
                {busy ? "Uploading…" : "Upload Photo"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {photos.length === 0 ? (
        <div className="border border-dashed border-[#D9CBBF] bg-white p-12 text-center text-[#5C5651]">
          <Camera size={28} strokeWidth={1.25} className="mx-auto mb-3 text-[#C9BBAE]" />
          No photos yet. Add a photo to start the gallery.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {photos.map((ph) => (
            <div key={ph.id} className="border border-[#EBE5E0] bg-white flex flex-col group" data-testid={`photo-card-${ph.id}`}>
              <div className="relative">
                <img src={fileSrc(ph.image_url)} alt={ph.title} className="w-full h-52 object-cover" />
                {canDelete(ph) && (
                  <button data-testid={`delete-photo-${ph.id}`} onClick={() => remove(ph.id)}
                    className="absolute top-2 right-2 bg-white/90 hover:bg-white text-[#5C5651] hover:text-[#9E4747] p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 size={15} strokeWidth={1.5} />
                  </button>
                )}
              </div>
              <div className="p-4 flex-1">
                <p className="text-sm text-[#2B2622] font-medium">{ph.title}</p>
                {ph.notes && <p className="text-sm text-[#5C5651] mt-1 leading-relaxed whitespace-pre-line">{ph.notes}</p>}
                <p className="text-[11px] uppercase tracking-wider text-[#A68A6B] mt-2">{ph.uploaded_by} · {fmtDate(ph.created_at)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
